import { DAMAGE, FINES, SCORE, repairCost } from '@accra/shared';
import type { EventBus } from '../core/events';
import { checkContact, isCloseCall } from '../systems/collision';
import { crossCarHitting } from '../systems/junctions/crossTraffic';
import { addBonus } from '../systems/scoring';
import { isActive, shieldSaves } from '../systems/pickups/powerUps';
import { canBeHit, knockCrossCar, knockTraffic } from '../systems/knock';
import { legAt } from '../world/route';
import type { RunState, TrafficCar } from './RunState';

const STUMBLE_WINDOW = 4;
/** Sitting still after a crash while you and the other driver sort it out. */
const RECOVER_SECONDS = 1.4;
/** After a crash, contacts are ignored this long so you can pull away. */
const GRACE_SECONDS = RECOVER_SECONDS + 1.2;

function breakDownIfWrecked(state: RunState, events: EventBus): void {
  if (state.bus.damage < DAMAGE.breakdownAt) return;
  state.over = true;
  state.endReason = 'breakdown';
  events.emit('run:brokeDown', { legName: legAt(state.bus.s).name });
}

/** A crash no longer ends the shift: you pay (repairs, or a fine for a rear-end), lose a rider or two, and carry on. */
export function collide(state: RunState, cause: string, rearEnd: boolean, events: EventBus): void {
  const { bus, stats } = state;
  const repairPesewas = rearEnd ? 0 : repairCost(cause);
  const finePesewas = rearEnd ? FINES.rearEnd : 0;
  stats.repairsPesewas += repairPesewas;
  stats.finesPesewas += finePesewas;
  if (!rearEnd) stats.repairsByKind[cause] = (stats.repairsByKind[cause] ?? 0) + 1;
  stats.crashes += 1;
  if (rearEnd) stats.rearEnds += 1;
  state.crashCause = cause;
  bus.damage = Math.min(DAMAGE.breakdownAt, bus.damage + DAMAGE.crash);
  bus.speed = 0;
  bus.targetLane = bus.lane;
  bus.recover = RECOVER_SECONDS;
  bus.invulnerable = GRACE_SECONDS;
  // Shaken riders get down without paying.
  state.passengers.splice(0, Math.min(state.passengers.length, 2));
  events.emit('run:collision', { cause, rearEnd, repairPesewas, finePesewas, damage: bus.damage });
  breakDownIfWrecked(state, events);
}

function scrape(state: RunState, car: TrafficCar, events: EventBus): void {
  const { bus } = state;
  if (bus.stumbleWindow > 0) {
    // Second scrape in a row: that's a proper crash.
    collide(state, car.kind.id, false, events);
    return;
  }
  const repairPesewas = repairCost('scrape');
  state.stats.repairsPesewas += repairPesewas;
  state.stats.scrapes += 1;
  state.stats.repairsByKind.scrape = (state.stats.repairsByKind.scrape ?? 0) + 1;
  bus.damage = Math.min(DAMAGE.breakdownAt, bus.damage + DAMAGE.scrape);
  bus.stumbleWindow = STUMBLE_WINDOW;
  bus.invulnerable = 0.6;
  bus.speed *= 0.4;
  bus.targetLane = bus.lane;
  state.passengers.pop();
  events.emit('run:stumbled', { cause: car.kind.id, repairPesewas });
  breakDownIfWrecked(state, events);
}

function closeCall(state: RunState, car: TrafficCar, events: EventBus): void {
  car.closeCallCounted = true;
  state.stats.closeCalls += 1;
  addBonus(state, SCORE.perCloseCall);
  events.emit('run:closeCall', { kind: car.kind.id });
}

const SIREN_BLAST_POINTS = 20;

/** Under the Siren Boost you blast through: the car gets out of the way and you earn points. */
function blasted(state: RunState, cause: string, events: EventBus): boolean {
  if (!isActive(state, 'siren')) return false;
  addBonus(state, SIREN_BLAST_POINTS);
  events.emit('powerUp:saved', { id: 'siren', cause });
  return true;
}

/** A crash, unless a power-up saves you. */
function hit(state: RunState, cause: string, rearEnd: boolean, events: EventBus): void {
  if (blasted(state, cause, events) || shieldSaves(state, cause, events)) return;
  collide(state, cause, rearEnd, events);
}

/** Contact between the trotro and traffic: crashes, scrapes and close calls. */
export function resolveHits(state: RunState, events: EventBus): void {
  const { bus } = state;
  if (bus.invulnerable > 0) return;
  const crossing = crossCarHitting(bus, state.crossTraffic.filter(canBeHit));
  if (crossing) {
    // The other car stays: it stops in the junction for a moment, then carries on.
    knockCrossCar(crossing);
    hit(state, crossing.kind.id, false, events);
    return;
  }
  for (const car of state.traffic) {
    if (!canBeHit(car)) continue;
    const contact = checkContact(bus, car);
    if (contact === 'hit') {
      // Hitting the back of a slower vehicle ahead of you is a rear-end: police fine instead of repairs.
      const rearEnd = !car.kind.hoppable && car.s > bus.s && bus.speed > car.speed;
      // The other vehicle stays on the road: shoved, swung round, stopped for a moment (barriers knocked over).
      knockTraffic(car, bus);
      hit(state, car.kind.hoppable ? 'barrier' : car.kind.id, rearEnd, events);
      return;
    }
    if (contact === 'graze') {
      if (!shieldSaves(state, car.kind.id, events)) scrape(state, car, events);
      return;
    }
    if (!car.closeCallCounted && isCloseCall(bus, car) && bus.speed > 8) closeCall(state, car, events);
  }
}
